import { useMemo, useState } from 'react';
import {
  EDGE_FUNCTIONS,
  buildFunctionsManifest,
  buildFunctionsDeployScript,
  functionCategoryClass,
} from '@/lib/edgeFunctions';
import { downloadEdgeFunctionsZip, listEdgeFunctions } from '@/lib/edgeFunctionSources';
import { downloadText } from '@/lib/dbAdmin';

const FN_BASE = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL as string}/functions/v1`;

export default function FunctionsPanel() {
  const [copied, setCopied] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [zipping, setZipping] = useState(false);
  const [zipStatus, setZipStatus] = useState('');

  const allFunctions = useMemo(() => listEdgeFunctions(), []);
  const total = allFunctions.length || EDGE_FUNCTIONS.length;

  const filtered = allFunctions.filter((fn) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return fn.name.toLowerCase().includes(q) || fn.slug.toLowerCase().includes(q) || fn.description.toLowerCase().includes(q);
  });

  const downloadZip = async () => {
    setZipping(true);
    setZipStatus('');
    try {
      const count = await downloadEdgeFunctionsZip();
      setZipStatus(`${count} fonction(s) exportée(s) dans le ZIP.`);
    } catch {
      setZipStatus('Export impossible pour le moment.');
    } finally {
      setZipping(false);
    }
  };

  const copy = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(`${FN_BASE}/${slug}`);
      setCopied(slug);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-terminal-box-line text-lg"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-950">Edge Functions du back-end</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {EDGE_FUNCTIONS.length} fonctions · source dans <code className="text-foreground-700">supabase/functions/&lt;slug&gt;/index.ts</code>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => void downloadZip()}
            disabled={zipping}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {zipping ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-download-2-line"></i>}
            Télécharger le code (ZIP)
          </button>
          <button
            onClick={() => downloadText(`zifek-edge-functions-${Date.now()}.json`, buildFunctionsManifest(), 'application/json')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold whitespace-nowrap hover:bg-secondary-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-file-code-line"></i>
            Manifeste JSON
          </button>
          <button
            onClick={() => downloadText('deploy-edge-functions.sh', buildFunctionsDeployScript(), 'text/x-shellscript')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-terminal-line"></i>
            Script déploiement
          </button>
        </div>
      </div>

      {zipStatus && (
        <p className="mb-3 text-[11px] text-accent-700 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-line"></i>{zipStatus}
        </p>
      )}

      <div className="relative mb-3">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher une fonction…"
          className="w-full h-10 pl-9 pr-4 rounded-md border border-background-200/70 bg-background-100 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {filtered.map((fn) => (
          <div key={fn.slug} className="border border-background-200/70 rounded-lg p-3.5 bg-background-100/60">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-foreground-950">{fn.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${functionCategoryClass(fn.category)}`}>
                    {fn.category}
                  </span>
                </div>
                <code className="text-[11px] text-foreground-500">{fn.slug}</code>
              </div>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                  fn.verifyJwt ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-700'
                }`}
                title={fn.verifyJwt ? 'JWT requis' : 'Public (sans JWT)'}
              >
                {fn.verifyJwt ? 'JWT' : 'Public'}
              </span>
            </div>
            <p className="text-xs text-foreground-500 mb-2.5 leading-relaxed">{fn.description}</p>
            <button
              onClick={() => copy(fn.slug)}
              className="inline-flex items-center gap-1.5 text-[11px] text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className={copied === fn.slug ? 'ri-check-line text-accent-600' : 'ri-file-copy-line'}></i>
              {copied === fn.slug ? 'URL copiée' : 'Copier l\u2019endpoint'}
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-foreground-400 py-6 text-center md:col-span-2">Aucune fonction correspondante.</p>
        )}
      </div>
      <p className="text-[11px] text-foreground-400 mt-3 flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5"></i>
        L&apos;export contient {total} fichier(s) <code>index.ts</code>, prêts à être redéployés. Les clés et secrets ne sont jamais inclus.
      </p>
    </div>
  );
}